"use client";

import { usePathname } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { BibleBookId } from "@packages/sdk";
import type { PropsWithChildren } from "react";
import { createContext, useEffect, useRef, useState } from "react";

type BibleEntryContext = {
  setData: (
    translationId: number | null,
    translationName: string | null,
    book: BibleBookId | null,
    chapter: number | string | null,
  ) => void;
  translationId: number | null;
  translationName: string | null;
  book: BibleBookId | null;
  chapter: number | string | null;
};

export const BibleEntry = createContext<BibleEntryContext>({
  setData: () => {},
  translationId: null,
  translationName: null,
  book: null,
  chapter: null,
});

export const BibleEntryTracker = ({ children }: PropsWithChildren) => {
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [book, setBook] = useState<BibleBookId | null>(null);
  const [translationId, setTranslationId] = useState<number | null>(null);
  const [translationName, setTranslationName] = useState<string | null>(null);
  const [chapter, setChapter] = useState<number | string | null>(null);
  const analytics = useAnalytics();
  const startInstant = useRef<number | null>(null);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const pathname = usePathname();
  const setData = (
    translationId: number,
    translationName: string,
    book: BibleBookId,
    chapter: number,
  ) => {
    setBook(book);
    setTranslationId(translationId);
    setTranslationName(translationName);
    setChapter(chapter);
  };

  useEffect(() => {
    const bibleEventProperties = {
      bible_book_id: book,
      bible_translation_id: translationId,
      bible_translation_name: translationName,
      bible_chapter_id: chapter,
    };

    // path change means we should start waiting again, or not wait at all
    clearTimeout(timer.current);

    if (pathname.includes("/bible") && !hasStarted) {
      timer.current = setTimeout(() => {
        setHasStarted(true);
        startInstant.current = Date.now();
        analytics.track({
          event: HallowAnalyticsEvent.BibleStarted,
          properties: bibleEventProperties,
        });
      }, 5000);
    } else if (
      !pathname.includes("/bible") &&
      hasStarted &&
      Date.now() - startInstant.current > 5000
    ) {
      setHasStarted(false);
      startInstant.current = null;
      analytics.track({
        event: HallowAnalyticsEvent.BibleCompleted,
        properties: bibleEventProperties,
      });
    }
  }, [pathname, book, chapter, translationId, translationName]);

  return (
    <BibleEntry.Provider
      value={{ setData, translationId, translationName, chapter, book }}
    >
      {children}
    </BibleEntry.Provider>
  );
};
